/**
 * Cross-app handoff to the admin/POS **panel**.
 *
 * The panel is a SEPARATE SPA: in the combined IIS deploy it is served
 * same-origin under `/panel/`; in local dev it runs on its own Vite server
 * (`:5174`). Staff/admin who sign in on the storefront's `/staff` screen belong
 * in that panel, not on the customer home — so we mirror their freshly-minted
 * session into the panel's redux-persist key and full-navigate there.
 */

/** Base URL of the panel app for the current environment. */
export function panelBaseUrl(): string {
  const dev = (import.meta as unknown as { env: { DEV: boolean } }).env.DEV;
  if (dev) return `${window.location.protocol}//${window.location.hostname}:5174/`;
  return '/panel/';
}

/**
 * Write the staff session into the panel's auth store, then return the panel URL
 * to redirect to. Both apps are same-origin in the deploy, so the panel rehydrates
 * straight into the dashboard with no second login. (Cross-origin dev can't share
 * storage, so there the panel falls back to its own login.)
 *
 * The shape AND the storage written here must match the panel store's
 * `authPersistConfig` (key `thalios-panel-auth`) and the `auth` slice state.
 *
 * This wrote to sessionStorage until 2026-08-09, which was correct until the panel
 * moved its persist storage to localStorage (so a discarded POS tab would stop
 * ending the shift). Nothing failed loudly — the write succeeded, the panel simply
 * read a different store, found no session, and asked for a username and password
 * immediately after a successful PIN sign-in.
 */
export function handoffStaffSession(payload: {
  user: { id: number; name: string; initials: string };
  token: string;
  refreshToken?: string;
  sessionExpiresAt?: string;
  role: string;
  permissions: string[];
}): string {
  try {
    // redux-persist stores each top-level slice field as its own JSON string.
    const persisted = {
      user: JSON.stringify(payload.user),
      // The refresh token travels with the handoff, or the panel would inherit a session it
      // cannot renew — and would sign the user out the moment the access token expired.
      session: JSON.stringify({
        token: payload.token,
        refreshToken: payload.refreshToken,
        sessionExpiresAt: payload.sessionExpiresAt
      }),
      role: JSON.stringify(payload.role),
      permissions: JSON.stringify(payload.permissions),
      _persist: JSON.stringify({ version: -1, rehydrated: true })
    };
    window.localStorage.setItem('persist:thalios-panel-auth', JSON.stringify(persisted));
    // Clear any session-storage copy left by an older build. redux-persist reads only the
    // configured store, but a stale key here is a confusing thing to find while debugging.
    window.sessionStorage.removeItem('persist:thalios-panel-auth');
  } catch {
    /* storage unavailable — the panel will simply ask for a login */
  }
  return panelBaseUrl();
}
